import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      const staffToken = localStorage.getItem("gh_auth_token");
      if (!staffToken) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        const { data } = await api.get("/auth/me", { timeout: 6000 });
        if (!cancelled) setUser(data);
      } catch {
        try {
          const { data } = await api.post("/auth/refresh", {}, { timeout: 6000 });
          if (data.access_token) {
            localStorage.setItem("gh_auth_token", data.access_token);
          }
          if (!cancelled) setUser(data.user);
        } catch {
          localStorage.removeItem("gh_auth_token");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) {
        localStorage.setItem("gh_auth_token", data.access_token);
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: errMsg(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* cookie already gone */
    }
    localStorage.removeItem("gh_auth_token");
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
